/**
 *  Copyright (C) 2022 - Jeci SARL - https://jeci.fr
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU Affero General Public License as
 *  published by the Free Software Foundation, either version 3 of the
 *  License, or (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU Affero General Public License for more details.
 *
 *  You should have received a copy of the GNU Affero General Public License
 *  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import { computed, watch, onUnmounted } from "vue";
import { useUploadStore } from "@/stores/upload.js";
import ghostMatcher from "@/services/GhostMatcher.js";

const GHOST_TTL = 5 * 60 * 1000; // 5 minutes

// First poll after 10s, then every 3s, up to MAX_RETRIES attempts.
const INITIAL_DELAY = 10000;
const POLL_DELAY = 3000;
const MAX_RETRIES = 20;

/**
 * Composable that provides the list of ghost nodes relevant to the
 * current display context and manages auto-refresh polling
 * (10s initial delay, then every 3s, up to 20 retries).
 *
 * @param {Object} options
 * @param {import('vue').Ref<Object>} options.displayContext - Reactive context:
 *   { type: 'view'|'folder'|'search'|'site-root', viewConfig, currentNodeId, siteId, entries }
 * @param {Function} options.onRefresh - Callback to trigger a data reload.
 * @returns {{ relevantGhosts: import('vue').ComputedRef<Array>, hasGhosts: import('vue').ComputedRef<boolean> }}
 */
export function useGhostNodes({ displayContext, onRefresh }) {
  const uploadStore = useUploadStore();
  let pollingTimer = null;
  let retryCount = 0;

  const relevantGhosts = computed(() => {
    const ctx = displayContext.value;
    if (!ctx) {
      return [];
    }

    // Clean expired ghosts on each evaluation
    uploadStore.cleanExpiredGhosts(GHOST_TTL);

    const now = Date.now();
    const pending = uploadStore.pendingNodes;

    return pending.filter((ghost) => {
      // Filter expired
      if (now - ghost._uploadedAt >= GHOST_TTL) {
        return false;
      }

      // Deduplicate: skip if already in current entries.
      // Supports both flat format ({ id }) used by navigationStore
      // and wrapped format ({ entry: { id } }) used by searchStore.
      const entries = ctx.entries;
      if (entries) {
        const entryList = Array.isArray(entries) ? entries : entries.value;
        if (
          entryList?.some((e) => {
            const id = e.id || e.entry?.id;
            return id === ghost.nodeId;
          })
        ) {
          return false;
        }
      }

      // Match by context type
      switch (ctx.type) {
        case "view": {
          // Scope ghosts to the parent node (e.g. consultation) when provided.
          // This prevents ghosts from consultation A appearing in consultation B.
          if (
            ctx.currentNodeId &&
            ghost._uploadContext?.parentNodeId &&
            ghost._uploadContext.parentNodeId !== ctx.currentNodeId
          ) {
            return false;
          }

          // Always show the ghost in the exact view it was uploaded from.
          const ghostViewId = ghost._uploadContext?.activeViewId;
          if (ghostViewId && ghostViewId === ctx.viewId) {
            return true;
          }

          // For other views: check site + criteria matching.
          // GhostMatcher now derives TYPE from isFile/isFolder and
          // evaluates legacy AFTS query strings, so false positives
          // are minimised.
          if (!ghostMatcher.matchesSite(ghost, ctx.siteId)) {
            return false;
          }
          return ghostMatcher.matchesViewConfig(ghost, ctx.viewConfig);
        }
        case "folder":
          return ghostMatcher.matchesFolder(ghost, ctx.currentNodeId);
        case "site-root":
          return ghostMatcher.matchesSite(ghost, ctx.siteId);
        case "search":
          return ghostMatcher.matchesSite(ghost, ctx.siteId);
        default:
          return false;
      }
    });
  });

  const hasGhosts = computed(() => relevantGhosts.value.length > 0);

  function stopPolling() {
    if (pollingTimer !== null) {
      clearTimeout(pollingTimer);
      pollingTimer = null;
    }
  }

  function scheduleNext() {
    stopPolling();
    const delay = retryCount === 0 ? INITIAL_DELAY : POLL_DELAY;
    pollingTimer = setTimeout(() => {
      pollingTimer = null;
      if (!hasGhosts.value) {
        return;
      }
      retryCount++;
      if (retryCount > MAX_RETRIES) {
        // Limit reached, stop polling
        return;
      }
      if (onRefresh) {
        onRefresh();
      }
      scheduleNext();
    }, delay);
  }

  function startPolling() {
    retryCount = 0;
    scheduleNext();
  }

  // Start/stop polling when ghosts appear/disappear
  watch(
    hasGhosts,
    (hasAny, hadAny) => {
      if (hasAny && !hadAny) {
        startPolling();
      } else if (!hasAny && hadAny) {
        stopPolling();
      }
    },
    { immediate: true },
  );

  // When new ghosts are added while polling is already running,
  // reset to the short initial delay so the first one is checked fast.
  watch(
    () => uploadStore.pendingNodes.length,
    (newLen, oldLen) => {
      if (newLen > oldLen && hasGhosts.value) {
        startPolling();
      }
    },
  );

  // Remove ghosts from pendingNodes once they appear in entries
  // (i.e., Solr has indexed them and the API returned them).
  watch(
    () => {
      const ctx = displayContext.value;
      if (!ctx?.entries) {
        return null;
      }
      const entryList = Array.isArray(ctx.entries)
        ? ctx.entries
        : ctx.entries?.value;
      return entryList;
    },
    (entryList) => {
      if (!entryList || entryList.length === 0) {
        return;
      }
      if (uploadStore.pendingNodes.length === 0) {
        return;
      }

      const entryIds = new Set(
        entryList.map((e) => e.id || e.entry?.id).filter(Boolean),
      );
      for (const ghost of [...uploadStore.pendingNodes]) {
        if (entryIds.has(ghost.nodeId)) {
          uploadStore.removeGhostNode(ghost.nodeId);
        }
      }
    },
  );

  onUnmounted(() => {
    stopPolling();
  });

  return {
    relevantGhosts,
    hasGhosts,
  };
}
